import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { navigationData } from "@/data/navigationData";
import { trackEvent } from "@/lib/firebase";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedMobileMenus, setExpandedMobileMenus] = useState({});
  const [expandedMobileCategories, setExpandedMobileCategories] = useState({});
  const [expandedMobileSubcategories, setExpandedMobileSubcategories] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle navigation with menu close
  const handleNavigation = useCallback((path) => {
    trackEvent("nav_click", {
      destination: path,
      from_path: location.pathname,
      mobile_menu_open: isMobileMenuOpen,
    });
    setActiveMenu(null);
    setActiveCategory(null);
    setIsMobileMenuOpen(false);
    setExpandedMobileMenus({});
    setExpandedMobileCategories({});
    setExpandedMobileSubcategories({});
    navigate(path);
  }, [isMobileMenuOpen, location.pathname, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMenu(null);
    setActiveCategory(null);
    setExpandedMobileMenus({});
    setExpandedMobileCategories({});
    setExpandedMobileSubcategories({});
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideNav = navRef.current && !navRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

      // Only close if click is outside both nav and dropdown
      if (isOutsideNav && (isOutsideDropdown || !dropdownRef.current)) {
        setActiveMenu(null);
        setActiveCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMenuEnter = useCallback((menuId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveMenu(menuId);
    // Set first category as active by default
    const menu = navigationData.find((m) => m.id === menuId);
    const menuCategories = menu && menu.categories ? menu.categories : [];
    if (menuCategories.length > 0) {
      setActiveCategory(menuCategories[0].id);
    }
  }, []);

  const handleMenuLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
      setActiveCategory(null);
    }, 150);
  }, []);

  const handleDropdownEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const toggleMobileMenu = (menuId) => {
    setExpandedMobileMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const toggleMobileCategory = (categoryId) => {
    setExpandedMobileCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleMobileSubcategory = (subcategoryId) => {
    setExpandedMobileSubcategories((prev) => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId],
    }));
  };

  const activeMenuData = navigationData.find((m) => m.id === activeMenu);
  // Extract categories safely without nested optional chaining
  const activeMenuCategories = activeMenuData && activeMenuData.categories ? activeMenuData.categories : [];
  const activeCategoryData = activeMenuCategories.find((c) => c.id === activeCategory) || null;
  // Get subcategories from active category
  const activeSubcategories = activeCategoryData && activeCategoryData.subcategories ? activeCategoryData.subcategories : [];

  return (
    <>
      <motion.nav
        ref={navRef}
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "glass py-3" : "bg-transparent py-4"
          }`}
      >
        <div className="w-full px-4 lg:px-6 xl:px-8 flex items-center justify-between gap-2">
          {/* Logo - Extreme Left */}

<Link
  to="/"
  data-testid="logo-link"
  className="shrink-0"
>
  <img
    src="/logo.png"
    alt="Finease Consulting"
    className="h-8 lg:h-10 md:h-12 w-auto scale-150 origin-left"
  />
</Link>


          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <div className="flex items-center gap-1">
              {navigationData.map((menu) => (
                <div
                  key={menu.id}
                  className="relative"
                  onMouseEnter={() => menu.categories && handleMenuEnter(menu.id)}
                  onMouseLeave={() => menu.categories && handleMenuLeave()}
                >
                  {menu.categories ? (
                    <button
                      data-testid={`nav-menu-${menu.id}`}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap cursor-pointer ${activeMenu === menu.id
                          ? "text-[#00C896] bg-[#00C896]/10"
                          : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                    >
                      {menu.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${activeMenu === menu.id ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                  ) : (
                    <button
                      data-testid={`nav-menu-${menu.id}`}
                      onClick={() => handleNavigation(menu.path)}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap cursor-pointer ${location.pathname === menu.path
                          ? "text-[#00C896]"
                          : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                    >
                      {menu.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button - Right */}
          <Link
            to="/contact"
            data-testid="nav-consultation-btn"
            className="hidden lg:inline-flex shrink-0 bg-[#00C896] text-black px-3 lg:px-4 py-2 rounded-full text-[11px] lg:text-xs xl:text-sm font-semibold hover:bg-[#00E6AC] transition-colors"
          >
            Contact
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => {
              trackEvent("mobile_menu_toggle", {
                next_state: !isMobileMenuOpen ? "open" : "close",
                from_path: location.pathname,
              });
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            className="lg:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Desktop Dropdown - Outside nav for proper fixed positioning */}
      <AnimatePresence>
        {activeMenu && activeMenuCategories.length > 0 && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`fixed left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5 shadow-2xl hidden lg:block pointer-events-auto`}
            style={{
              top: isScrolled ? '56px' : '64px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleMenuLeave}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
              <div className="grid grid-cols-12 gap-6">
                {/* Categories Column (Registrations, Incorporations, etc.) */}
                <div className="col-span-3 border-r border-white/5 pr-4">
                  <p className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4">
                    Service Categories
                  </p>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {activeMenuCategories.map((category) => (
                      <button
                        key={category.id}
                        onMouseEnter={() => setActiveCategory(category.id)}
                        onClick={() => setActiveCategory(category.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors cursor-pointer ${activeCategory === category.id
                            ? "bg-[#00C896]/10 text-white"
                            : "text-slate-400 hover:text-white hover:bg-white/5"
                          }`}
                      >
                        <span className="text-sm font-medium">
                          {category.label}
                        </span>
                        <ChevronRight
                          size={14}
                          className={`transition-opacity ${activeCategory === category.id
                              ? "opacity-100 text-[#00C896]"
                              : "opacity-0"
                            }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subcategories & Services */}
                <div className="col-span-9 pl-2">
                  <AnimatePresence mode="wait">
                    {activeCategoryData && (
                      <motion.div
                        key={activeCategory}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.15 }}
                      >
                        <p className="text-xs font-mono tracking-wider uppercase text-slate-500 mb-4">
                          {activeCategoryData.label}
                        </p>
                        <div className="grid grid-cols-3 gap-6 max-h-[350px] overflow-y-auto">
                          {activeSubcategories.map((subcategory) => (
                            <div key={subcategory.id}>
                              {/* Subcategory Label (Business, NGO, Companies, Conversions, etc.) */}
                              <p className="text-xs font-semibold text-[#00C896] mb-3 uppercase tracking-wide">
                                {subcategory.label}
                              </p>
                              {/* Services under this subcategory */}
                              <div className="space-y-1">
                                {subcategory.services.map((service) => (
                                  <div
                                    key={service.id}
                                    data-testid={`nav-service-${service.id}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleNavigation(service.path);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    className="group p-2 rounded-lg hover:bg-white/5 transition-colors text-left cursor-pointer"
                                  >
                                    <p className="text-sm font-medium text-white group-hover:text-[#00C896] transition-colors">
                                      {service.label}
                                    </p>
                                    <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                                      {service.description}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden mobile-menu-overlay bg-[#050505]/95"
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0A0A0A] border-l border-white/10 pt-20 overflow-y-auto"
            >
              <div className="px-6 pb-8">
                {/* Mobile Navigation Items */}
                <div className="space-y-1">
                  {navigationData.map((menu, index) => (
                    <motion.div
                      key={menu.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/5 last:border-0"
                    >
                      {menu.categories ? (
                        <>
                          <button
                            onClick={() => toggleMobileMenu(menu.id)}
                            className="w-full flex items-center justify-between py-4 text-left"
                          >
                            <span className="text-lg font-medium text-white">
                              {menu.label}
                            </span>
                            <ChevronDown
                              size={18}
                              className={`text-slate-400 transition-transform duration-200 ${expandedMobileMenus[menu.id] ? "rotate-180" : ""
                                }`}
                            />
                          </button>
                          <AnimatePresence>
                            {expandedMobileMenus[menu.id] && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="pb-4 pl-4 space-y-1">
                                  {menu.categories.map((category) => (
                                    <div key={category.id}>
                                      <button
                                        onClick={() =>
                                          toggleMobileCategory(category.id)
                                        }
                                        className="w-full flex items-center justify-between py-2 text-left"
                                      >
                                        <span className="text-sm font-medium text-[#00C896]">
                                          {category.label}
                                        </span>
                                        <ChevronRight
                                          size={14}
                                          className={`text-slate-500 transition-transform duration-200 ${expandedMobileCategories[
                                              category.id
                                            ]
                                              ? "rotate-90"
                                              : ""
                                            }`}
                                        />
                                      </button>
                                      <AnimatePresence>
                                        {expandedMobileCategories[
                                          category.id
                                        ] && (
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{
                                                height: "auto",
                                                opacity: 1,
                                              }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.15 }}
                                              className="overflow-hidden"
                                            >
                                              <div className="pl-4 pb-2 space-y-1">
                                                {category.subcategories && category.subcategories.map(
                                                  (subcategory) => (
                                                    <div key={subcategory.id}>
                                                      <button
                                                        onClick={() =>
                                                          toggleMobileSubcategory(subcategory.id)
                                                        }
                                                        className="w-full flex items-center justify-between py-2 text-left"
                                                      >
                                                        <span className="text-xs font-medium text-slate-300 uppercase tracking-wide">
                                                          {subcategory.label}
                                                        </span>
                                                        <ChevronRight
                                                          size={12}
                                                          className={`text-slate-500 transition-transform duration-200 ${expandedMobileSubcategories[
                                                              subcategory.id
                                                            ]
                                                              ? "rotate-90"
                                                              : ""
                                                            }`}
                                                        />
                                                      </button>
                                                      <AnimatePresence>
                                                        {expandedMobileSubcategories[
                                                          subcategory.id
                                                        ] && (
                                                            <motion.div
                                                              initial={{ height: 0, opacity: 0 }}
                                                              animate={{
                                                                height: "auto",
                                                                opacity: 1,
                                                              }}
                                                              exit={{ height: 0, opacity: 0 }}
                                                              transition={{ duration: 0.15 }}
                                                              className="overflow-hidden"
                                                            >
                                                              <div className="pl-4 pb-2 space-y-1">
                                                                {subcategory.services.map(
                                                                  (service) => (
                                                                    <button
                                                                      key={service.id}
                                                                      data-testid={`mobile-nav-${service.id}`}
                                                                      onClick={() => handleNavigation(service.path)}
                                                                      className="block w-full text-left py-2 text-sm text-slate-400 hover:text-white transition-colors"
                                                                    >
                                                                      {service.label}
                                                                    </button>
                                                                  )
                                                                )}
                                                              </div>
                                                            </motion.div>
                                                          )}
                                                      </AnimatePresence>
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            </motion.div>
                                          )}
                                      </AnimatePresence>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <button
                          onClick={() => handleNavigation(menu.path)}
                          className="block w-full text-left py-4 text-lg font-medium text-white hover:text-[#00C896] transition-colors"
                        >
                          {menu.label}
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 pt-6 border-t border-white/10"
                >
                  <button
                    onClick={() => handleNavigation('/contact')}
                    data-testid="mobile-consultation-btn"
                    className="block w-full text-center border border-[#00C896]/50 text-[#00C896] px-6 py-3 rounded-full text-base font-medium hover:bg-[#00C896]/10 transition-colors"
                  >
                    Request Consultation
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
